import React, { useState, useEffect } from 'react';
import { Globe, Coins, Sun, Moon, Check, ChevronRight, PartyPopper, MapPin } from 'lucide-react';
import { useTheme, useLanguage, useCurrency, useAuth, useIsland } from '../App';
import axios from '../lib/axios';
import confetti from 'canvas-confetti';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WelcomeFlow = () => {
  const [step, setStep] = useState(1);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const { user, refreshUser } = useAuth();
  const { theme, setTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  const { currency, setCurrency } = useCurrency();
  const { currentIsland, setCurrentIsland } = useIsland();

  const isDark = theme === 'dark';
  const totalSteps = 5; // Language, Location, Currency, Theme, Finish

  // Check if user needs onboarding
  useEffect(() => {
    if (user && user.is_new_user === true) {
      const timer = setTimeout(() => {
        setIsAnimating(true);
        setTimeout(() => setIsVisible(true), 100);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // Translations for each step
  const translations = {
    en: {
      step1Title: 'Choose Your Language',
      step1Subtitle: 'How would you like to use Your Buy Site?',
      step2Title: 'Your Location',
      step2Subtitle: 'Where do you want to browse items?',
      step3Title: 'Choose Your Currency',
      step3Subtitle: 'How do you want to see prices?',
      step3Usd: 'US Dollar',
      step3UsdDesc: 'International',
      step3Xcg: 'XCG',
      step3XcgDesc: 'Curaçao Guilder',
      step4Title: 'Choose Your Style',
      step4Subtitle: 'Pick your preferred appearance',
      step4Light: 'Light',
      step4Dark: 'Dark',
      finishTitle: "You're All Set!",
      finishSubtitle: 'Welcome to Your Buy Site. Start exploring!',
      next: 'Next',
      finish: 'Start Exploring',
      skip: 'Skip for now',
      canChange: 'You can always change these settings later.'
    },
    nl: {
      step1Title: 'Kies Je Taal',
      step1Subtitle: 'Hoe wil je Your Buy Site gebruiken?',
      step2Title: 'Jouw Locatie',
      step2Subtitle: 'Waar wil je items bekijken?',
      step3Title: 'Kies Je Valuta',
      step3Subtitle: 'Hoe wil je prijzen zien?',
      step3Usd: 'US Dollar',
      step3UsdDesc: 'Internationaal',
      step3Xcg: 'XCG',
      step3XcgDesc: 'Curaçao Gulden',
      step4Title: 'Kies Je Stijl',
      step4Subtitle: 'Kies je favoriete weergave',
      step4Light: 'Licht',
      step4Dark: 'Donker',
      finishTitle: 'Je Bent Klaar!',
      finishSubtitle: 'Welkom bij Your Buy Site. Veel plezier!',
      next: 'Volgende',
      finish: 'Begin met Verkennen',
      skip: 'Sla over',
      canChange: 'Je kunt dit later altijd aanpassen.'
    },
    es: {
      step1Title: 'Elige Tu Idioma',
      step1Subtitle: '¿Cómo quieres usar Your Buy Site?',
      step2Title: 'Tu Ubicación',
      step2Subtitle: '¿Dónde quieres ver artículos?',
      step3Title: 'Elige Tu Moneda',
      step3Subtitle: '¿Cómo quieres ver los precios?',
      step3Usd: 'Dólar US',
      step3UsdDesc: 'Internacional',
      step3Xcg: 'XCG',
      step3XcgDesc: 'Florín de Curazao',
      step4Title: 'Elige Tu Estilo',
      step4Subtitle: 'Elige tu apariencia preferida',
      step4Light: 'Claro',
      step4Dark: 'Oscuro',
      finishTitle: '¡Todo Listo!',
      finishSubtitle: 'Bienvenido a Your Buy Site. ¡Explora!',
      next: 'Siguiente',
      finish: 'Empezar a Explorar',
      skip: 'Omitir',
      canChange: 'Puedes cambiar esto más tarde.'
    }
  };

  const t = translations[language] || translations.en;

  // Language options
  const languageOptions = [
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' }
  ];

  // Island options
  const islandOptions = [
    { code: 'curacao', name: 'Curaçao', flag: '🇨🇼' },
    { code: 'aruba', name: 'Aruba', flag: '🇦🇼' },
    { code: 'bonaire', name: 'Bonaire', flag: '🇧🇶' }
  ];

  // Handlers
  const handleLanguageSelect = (langCode) => setLanguage(langCode);
  const handleIslandSelect = (islandCode) => setCurrentIsland(islandCode);
  const handleCurrencySelect = (curr) => setCurrency(curr);
  const handleThemeSelect = (newTheme) => setTheme(newTheme);

  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const completeOnboarding = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/profile`, {
        preferred_language: language,
        preferred_currency: currency,
        preferred_theme: theme,
        preferred_island: currentIsland,
        is_new_user: false
      });

      // Confetti!
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });

      if (refreshUser) {
        await refreshUser();
      }

      setTimeout(() => {
        setIsVisible(false);
        setTimeout(() => {
          setIsAnimating(false);
          // Dispatch event to trigger any pending ShareWin popup
          window.dispatchEvent(new CustomEvent('welcomeFlowComplete'));
        }, 300);
      }, 1500);

    } catch (error) {
      console.error('Error completing onboarding:', error);
      setIsVisible(false);
      // Still dispatch event on error so flow continues
      window.dispatchEvent(new CustomEvent('welcomeFlowComplete'));
    } finally {
      setSaving(false);
    }
  };

  const skipOnboarding = async () => {
    try {
      await axios.put(`${API}/auth/profile`, { is_new_user: false });
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error skipping onboarding:', error);
    }
    setIsVisible(false);
    setTimeout(() => {
      setIsAnimating(false);
      // Dispatch event to trigger any pending ShareWin popup
      window.dispatchEvent(new CustomEvent('welcomeFlowComplete'));
    }, 300);
  };

  if (!isAnimating || !user?.is_new_user) return null;

  return (
    <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`} />

      {/* Modal */}
      <div className={`relative w-full max-w-sm transform transition-all duration-300 ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}`}>
        <div className={`rounded-2xl overflow-hidden shadow-2xl ${isDark ? 'bg-slate-900 border border-white/10' : 'bg-white'}`}>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-1.5 pt-5 pb-2">
            {[1, 2, 3, 4].map((num) => (
              <div
                key={num}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  num === step
                    ? 'w-6 bg-primary'
                    : num < step
                    ? 'w-1.5 bg-primary'
                    : `w-1.5 ${isDark ? 'bg-white/20' : 'bg-gray-200'}`
                }`}
              />
            ))}
          </div>

          {/* Content */}
          <div className="p-5 pt-3">
            
            {/* Step 1: Language */}
            {step === 1 && (
              <div className="text-center animate-in fade-in slide-in-from-right-4 duration-300">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-50'}`}>
                  <Globe className="w-6 h-6 text-blue-500" />
                </div>
                <h2 className={`text-lg font-bold mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t.step1Title}
                </h2>
                <p className={`text-xs mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t.step1Subtitle}
                </p>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {languageOptions.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageSelect(lang.code)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        language === lang.code
                          ? 'border-primary bg-primary/10'
                          : isDark
                          ? 'border-white/10 hover:border-white/30 bg-white/5'
                          : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                      }`}
                    >
                      <span className="text-xl block">{lang.flag}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {lang.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2: Location */}
            {step === 2 && (
              <div className="text-center animate-in fade-in slide-in-from-right-4 duration-300">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-500/20' : 'bg-emerald-50'}`}>
                  <MapPin className="w-6 h-6 text-emerald-500" />
                </div>
                <h2 className={`text-lg font-bold mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t.step2Title}
                </h2>
                <p className={`text-xs mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t.step2Subtitle}
                </p>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  {islandOptions.map((island) => (
                    <button
                      key={island.code}
                      onClick={() => handleIslandSelect(island.code)}
                      className={`p-3 rounded-xl border-2 transition-all ${
                        currentIsland === island.code
                          ? 'border-primary bg-primary/10'
                          : isDark
                          ? 'border-white/10 hover:border-white/30 bg-white/5'
                          : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                      }`}
                    >
                      <span className="text-xl block">{island.flag}</span>
                      <span className={`text-xs font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {island.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Currency */}
            {step === 3 && (
              <div className="text-center animate-in fade-in slide-in-from-right-4 duration-300">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${isDark ? 'bg-amber-500/20' : 'bg-amber-50'}`}>
                  <Coins className="w-6 h-6 text-amber-500" />
                </div>
                <h2 className={`text-lg font-bold mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t.step3Title}
                </h2>
                <p className={`text-xs mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t.step3Subtitle}
                </p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <button
                    onClick={() => handleCurrencySelect('USD')}
                    className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                      currency === 'USD'
                        ? 'border-primary bg-primary/10'
                        : isDark
                        ? 'border-white/10 hover:border-white/30 bg-white/5'
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                      <span className="text-base font-bold text-green-600">$</span>
                    </div>
                    <div className="text-left min-w-0">
                      <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.step3Usd}</p>
                      <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t.step3UsdDesc}</p>
                    </div>
                  </button>

                  <button
                    onClick={() => handleCurrencySelect('XCG')}
                    className={`p-3 rounded-xl border-2 flex items-center gap-2 transition-all ${
                      currency === 'XCG'
                        ? 'border-primary bg-primary/10'
                        : isDark
                        ? 'border-white/10 hover:border-white/30 bg-white/5'
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-amber-500/20' : 'bg-amber-100'}`}>
                      <span className="text-base font-bold text-amber-600">ƒ</span>
                    </div>
                    <div className="text-left min-w-0">
                      <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.step3Xcg}</p>
                      <p className={`text-xs truncate ${isDark ? 'text-white/50' : 'text-gray-500'}`}>{t.step3XcgDesc}</p>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Theme */}
            {step === 4 && (
              <div className="text-center animate-in fade-in slide-in-from-right-4 duration-300">
                <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${isDark ? 'bg-purple-500/20' : 'bg-purple-50'}`}>
                  {isDark ? <Moon className="w-6 h-6 text-purple-400" /> : <Sun className="w-6 h-6 text-purple-500" />}
                </div>
                <h2 className={`text-lg font-bold mb-0.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t.step4Title}
                </h2>
                <p className={`text-xs mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t.step4Subtitle}
                </p>

                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    onClick={() => handleThemeSelect('light')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      theme === 'light'
                        ? 'border-primary bg-primary/10'
                        : isDark
                        ? 'border-white/10 hover:border-white/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="w-full aspect-[3/2] rounded-lg bg-white border border-gray-200 mb-2 flex items-center justify-center shadow-inner">
                      <Sun className="w-6 h-6 text-amber-500" />
                    </div>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {t.step4Light}
                    </span>
                  </button>

                  <button
                    onClick={() => handleThemeSelect('dark')}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      theme === 'dark'
                        ? 'border-primary bg-primary/10'
                        : isDark
                        ? 'border-white/10 hover:border-white/30'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="w-full aspect-[3/2] rounded-lg bg-slate-900 border border-slate-700 mb-2 flex items-center justify-center shadow-inner">
                      <Moon className="w-6 h-6 text-purple-400" />
                    </div>
                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {t.step4Dark}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Step 5: Finish */}
            {step === 5 && (
              <div className="text-center animate-in fade-in zoom-in-95 duration-300">
                <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-lg shadow-green-500/30">
                  <PartyPopper className="w-8 h-8 text-white" />
                </div>
                <h2 className={`text-xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t.finishTitle}
                </h2>
                <p className={`text-sm mb-4 ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {t.finishSubtitle}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="space-y-2">
              {step < 5 ? (
                <button
                  onClick={nextStep}
                  className="w-full py-3 px-4 bg-primary text-primary-foreground font-semibold rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
                >
                  {t.next}
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={completeOnboarding}
                  disabled={saving}
                  className="w-full py-3 px-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {t.finish}
                      <PartyPopper className="w-4 h-4" />
                    </>
                  )}
                </button>
              )}

              {step < 5 && (
                <button
                  onClick={skipOnboarding}
                  className={`w-full py-2 text-xs transition-colors ${isDark ? 'text-white/40 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {t.skip}
                </button>
              )}
            </div>

            {/* Helper text */}
            <p className={`text-xs text-center mt-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>
              {t.canChange}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeFlow;
